// FiltersPanel.jsx (Client Component)
'use client';

import { useMemo, useState, useEffect, useCallback, useId } from 'react';
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { byPrefixAndName } from '@awesome.me/kit-12cf79935c/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Checkbox from '@/components/ui/input/Checkbox';
import Radio from '@/components/ui/input/Radio';
import ToggleButton from '@/components/ui/input/ToggleButton';
import Input from '@/components/ui/input/Input';
import { Button } from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import clsx from 'clsx';
import {
  useHotelSearchFilterDataLayer,
  SEARCH_FILTER_CATEGORY_MAP,
} from '@/components/pages/hotel/search/hooks/useHotelSearchFilterDataLayer';

// Amenity code to icon mapping (CardItem.jsx'ten alındı)
const amenityIconMap = {
  3: "circle-wifi",
  7: "pet-custom",
  8: "wheelchair-1",
  9: "circle-parking",
  11: "vault",
  13: "water-ladder-1-1",
  22: "child-hotel",
  23: "air-conditioner",
  27: "bus-service-1",
  28: "umbrella-beach-1",
  30: "hot-tub-person",
  39: "water-ladder-1-1",
  60: "water-ladder-1-1",
  85: "unlock-room",
  88: "cstm-smarttv",
  89: "custom-nespresso",
  107: "hotel-building-new",
  159: "person-swimming-child-1",
  186: "charging-station-1",
  247: "water",
  257: "spa-1",
  267: "shower",
  268: "shower",
  269: "bath",
  270: "hair-dryer",
  272: "mini-bar",
  292: "terrace-new",
  295: "leak-sea",
  296: "leak-sea",
  297: "tree",
  302: "airplane-otel",
  372: "mountbig",
  373: "blueflag",
  404: "tulip",
  563: "umbrella-beach-1",
  720: "circle-parking",
  721: "circle-parking",
  727: "circle-parking"
};

/**
 * Hotel arama filtre paneli.
 *
 * @param {Object} props
 * @param {Object} props.filters - Mevcut filtre değerleri
 * @param {Function} props.onFilterChange - Filtre güncellendiğinde çağrılır
 * @param {Object} props.filterOptions - Response'tan gelen dinamik filtre seçenekleri
 * @param {Array} props.filterOptions.locations - İlçe/Bölge listesi
 * @param {Array} props.filterOptions.boards - Pansiyon tipleri
 * @param {Array} props.filterOptions.themes - Temalar
 * @param {Array} props.filterOptions.amenities - Tesis özellikleri
 * @param {Object} props.filterOptions.price - Fiyat aralığı (MinPrice, MaxPrice)
 */
export default function FiltersPanel({
  filters = {},
  onFilterChange = () => { },
  filterOptions = {},
  currentSortId = '0',
}) {
  const ratingGroupName = useId();
  const tHotel = useTranslationDetector('hotel');
  const {
    stars = [],
    boards = [],
    chains = [],
    locations = [],
    themes = [],
    amenities = [],
    priceMin = '',
    priceMax = '',
    rating = '',
    onlyDiscounted = false,
    onlyRefundable = false,
  } = filters;

  const { sendFilterAppliedEvent, getSortingType } =
    useHotelSearchFilterDataLayer();
  const sortingType = getSortingType(currentSortId);

  // Fiyat formatlama helper fonksiyonları (state'lerden önce tanımlanmalı)
  const formatPriceDisplay = useCallback((value) => {
    if (!value || value === '') return '';
    // Noktaları ve boşlukları temizle, sadece rakamları al
    const cleanValue = value.toString().replace(/[^\d]/g, '');
    if (!cleanValue) return '';
    // Türkçe formatlama: 7500 -> 7.500
    const number = parseInt(cleanValue, 10);
    if (isNaN(number)) return '';
    return number.toLocaleString('tr-TR');
  }, []);

  const cleanPriceValue = useCallback((value) => {
    if (!value || value === '') return '';
    // Tüm formatlamayı temizle, sadece rakamları al
    return value.toString().replace(/[^\d]/g, '');
  }, []);

  // Fiyat input'ları için local state (formatlanmış değerlerle)
  const [localPriceMin, setLocalPriceMin] = useState(() => formatPriceDisplay(priceMin || ''));
  const [localPriceMax, setLocalPriceMax] = useState(() => formatPriceDisplay(priceMax || ''));
  const [priceRangeError, setPriceRangeError] = useState('');

  // "Daha fazla göster" state'leri
  const [showAllLocations, setShowAllLocations] = useState(false);
  const [showAllBoards, setShowAllBoards] = useState(false);
  const [showAllChains, setShowAllChains] = useState(false);
  const [showAllThemes, setShowAllThemes] = useState(false);
  const [showAllAmenities, setShowAllAmenities] = useState(false);

  // priceMin/priceMax değiştiğinde local state'i güncelle (formatlanmış şekilde)
  useEffect(() => {
    setLocalPriceMin(formatPriceDisplay(priceMin || ''));
    setLocalPriceMax(formatPriceDisplay(priceMax || ''));
  }, [priceMin, priceMax, formatPriceDisplay]);

  // Board ID'lerini label'a çevir
  const getBoardLabel = (boardId) => {
    const boardMap = {
      'BB': 'boardBB',
      'RO': 'boardRO',
      'AI': 'boardAI',
      'HB': 'boardHB',
      'FB': 'boardFB',
      'UAI': 'boardUAI',
    };
    const key = boardMap[boardId];
    return key ? tHotel(key) : boardId;
  };

  // Aktif filtreleri hesapla
  const activeFilters = useMemo(() => {
    const active = [];

    // Yıldız filtreleri
    if (stars && stars.length > 0) {
      stars.forEach((star) => {
        active.push({
          type: 'star',
          value: star,
          label: tHotel('starsFilter', { star }),
          key: `star-${star}`,
        });
      });
    }

    // İlçe/Bölge filtreleri
    if (locations && locations.length > 0) {
      locations.forEach((locationId) => {
        const location = filterOptions.locations?.find(l => l.Id === locationId);
        if (location) {
          active.push({
            type: 'location',
            value: locationId,
            label: location.Name,
            key: `location-${locationId}`,
          });
        }
      });
    }

    // Pansiyon tipi filtreleri
    if (boards && boards.length > 0) {
      boards.forEach((boardId) => {
        active.push({
          type: 'board',
          value: boardId,
          label: getBoardLabel(boardId),
          key: `board-${boardId}`,
        });
      });
    }

    // Zincir filtreleri
    if (chains && chains.length > 0) {
      chains.forEach((chainSlug) => {
        const chain = filterOptions.chains?.find((c) => c.Slug === chainSlug);
        active.push({
          type: 'chain',
          value: chainSlug,
          label: chain?.Name || chainSlug,
          key: `chain-${chainSlug}`,
        });
      });
    }

    // Tema filtreleri
    if (themes && themes.length > 0) {
      themes.forEach((themeId) => {
        const theme = filterOptions.themes?.find(t => t.Id === themeId);
        if (theme) {
          active.push({
            type: 'theme',
            value: themeId,
            label: theme.Name,
            key: `theme-${themeId}`,
          });
        }
      });
    }

    // Tesis özellikleri filtreleri
    if (amenities && amenities.length > 0) {
      amenities.forEach((amenityId) => {
        const amenity = filterOptions.amenities?.find(a => a.Id === amenityId);
        if (amenity) {
          active.push({
            type: 'amenity',
            value: amenityId,
            label: amenity.Name,
            key: `amenity-${amenityId}`,
          });
        }
      });
    }

    // Fiyat aralığı
    if (priceMin || priceMax) {
      const min = priceMin ? Number(priceMin).toLocaleString('tr-TR') : '0';
      const max = priceMax ? Number(priceMax).toLocaleString('tr-TR') : '∞';
      active.push({
        type: 'price',
        value: { min: priceMin, max: priceMax },
        label: `${min} - ${max} TL`,
        key: 'price-range',
      });
    }

    // Değerlendirme puanı
    if (rating) {
      const ratingLabels = {
        '9+': `${tHotel('Excellent')} 9+`,
        '8+': `${tHotel('Great')} 8+`,
        '7+': `${tHotel('VeryGood')} 7+`,
        '6+': `${tHotel('Good')} 6+`,
      };
      active.push({
        type: 'rating',
        value: rating,
        label: ratingLabels[rating] || rating,
        key: 'rating',
      });
    }

    // Kampanyalı oteller
    if (onlyDiscounted) {
      active.push({
        type: 'discounted',
        value: true,
        label: tHotel('onlyDiscountedHotelsFilter'),
        key: 'discounted',
      });
    }

    // Rezervasyon koşulları (Ücretsiz iptal)
    if (onlyRefundable) {
      active.push({
        type: 'refundable',
        value: true,
        label: tHotel('freeCancellationAvailable'),
        key: 'refundable',
      });
    }

    return active;
  }, [stars, locations, boards, chains, themes, amenities, priceMin, priceMax, rating, onlyDiscounted, onlyRefundable, filterOptions, tHotel]);

  const activeFiltersCount = activeFilters.length;

  // Filtre kaldırma fonksiyonu
  const removeFilter = (filterToRemove) => {
    const updatedFilters = { ...filters };

    switch (filterToRemove.type) {
      case 'star':
        updatedFilters.stars = (updatedFilters.stars || []).filter(
          (s) => s !== filterToRemove.value
        );
        break;
      case 'location':
        updatedFilters.locations = (updatedFilters.locations || []).filter(
          (l) => l !== filterToRemove.value
        );
        break;
      case 'board':
        updatedFilters.boards = (updatedFilters.boards || []).filter(
          (b) => b !== filterToRemove.value
        );
        break;
      case 'chain':
        updatedFilters.chains = (updatedFilters.chains || []).filter(
          (c) => c !== filterToRemove.value
        );
        break;
      case 'theme':
        updatedFilters.themes = (updatedFilters.themes || []).filter(
          (t) => t !== filterToRemove.value
        );
        break;
      case 'amenity':
        updatedFilters.amenities = (updatedFilters.amenities || []).filter(
          (a) => a !== filterToRemove.value
        );
        break;
      case 'price':
        updatedFilters.priceMin = '';
        updatedFilters.priceMax = '';
        setLocalPriceMin('');
        setLocalPriceMax('');
        break;
      case 'rating':
        updatedFilters.rating = '';
        break;
      case 'discounted':
        updatedFilters.onlyDiscounted = false;
        break;
      case 'refundable':
        updatedFilters.onlyRefundable = false;
        break;
    }

    onFilterChange(updatedFilters);
  };

  // Tüm filtreleri temizle
  const clearAllFilters = () => {
    const clearedFilters = {
      stars: [],
      boards: [],
      chains: [],
      locations: [],
      themes: [],
      amenities: [],
      priceMin: '',
      priceMax: '',
      rating: '',
      onlyDiscounted: false,
      onlyRefundable: false,
    };
    setLocalPriceMin('');
    setLocalPriceMax('');
    onFilterChange(clearedFilters);
  };

  // Yıldız sayısı değişikliği
  const handleStarChange = (starValue, checked) => {
    let newStars = [...(stars || [])];
    if (checked) {
      if (!newStars.includes(starValue)) {
        newStars.push(starValue);
        const subcategory =
          starValue === 0 ? tHotel('unrated') : tHotel('starsFilter', { star: starValue });
        sendFilterAppliedEvent(
          SEARCH_FILTER_CATEGORY_MAP.star,
          subcategory,
          sortingType
        );
      }
    } else {
      newStars = newStars.filter((s) => s !== starValue);
    }
    onFilterChange((prevFilters) => ({ ...prevFilters, stars: newStars }));
  };

  // İlçe/Bölge değişikliği
  const handleLocationChange = (locationId, checked) => {
    let newLocations = [...(locations || [])];
    if (checked) {
      if (!newLocations.includes(locationId)) {
        newLocations.push(locationId);
        const location = filterOptions.locations?.find((l) => l.Id === locationId);
        const subcategory = location?.Name ?? String(locationId);
        sendFilterAppliedEvent(
          SEARCH_FILTER_CATEGORY_MAP.location,
          subcategory,
          sortingType
        );
      }
    } else {
      newLocations = newLocations.filter((l) => l !== locationId);
    }
    onFilterChange((prevFilters) => ({ ...prevFilters, locations: newLocations }));
  };

  // Pansiyon tipi değişikliği
  const handleBoardChange = (boardId, checked) => {
    let newBoards = [...(boards || [])];
    if (checked) {
      if (!newBoards.includes(boardId)) {
        newBoards.push(boardId);
        const subcategory = getBoardLabel(boardId);
        sendFilterAppliedEvent(
          SEARCH_FILTER_CATEGORY_MAP.board,
          subcategory,
          sortingType
        );
      }
    } else {
      newBoards = newBoards.filter((b) => b !== boardId);
    }
    onFilterChange((prevFilters) => ({ ...prevFilters, boards: newBoards }));
  };

  // Tema değişikliği
  const handleThemeChange = (themeId, checked) => {
    let newThemes = [...(themes || [])];
    if (checked) {
      if (!newThemes.includes(themeId)) {
        newThemes.push(themeId);
        const theme = filterOptions.themes?.find((t) => t.Id === themeId);
        const subcategory = theme?.Name ?? String(themeId);
        sendFilterAppliedEvent(
          SEARCH_FILTER_CATEGORY_MAP.theme,
          subcategory,
          sortingType
        );
      }
    } else {
      newThemes = newThemes.filter((t) => t !== themeId);
    }
    onFilterChange((prevFilters) => ({ ...prevFilters, themes: newThemes }));
  };

  // Zincir değişikliği
  const handleChainChange = (chainSlug, checked) => {
    let newChains = [...(chains || [])];
    if (checked) {
      if (!newChains.includes(chainSlug)) {
        newChains.push(chainSlug);
        const chain = filterOptions.chains?.find((c) => c.Slug === chainSlug);
        const subcategory = chain?.Name ?? String(chainSlug);
        sendFilterAppliedEvent(
          SEARCH_FILTER_CATEGORY_MAP.chain,
          subcategory,
          sortingType
        );
      }
    } else {
      newChains = newChains.filter((c) => c !== chainSlug);
    }
    onFilterChange((prevFilters) => ({ ...prevFilters, chains: newChains }));
  };

  // Tesis özelliği değişikliği
  const handleAmenityChange = (amenityId, checked) => {
    let newAmenities = [...(amenities || [])];
    if (checked) {
      if (!newAmenities.includes(amenityId)) {
        newAmenities.push(amenityId);
        const amenity = filterOptions.amenities?.find((a) => a.Id === amenityId);
        const subcategory = amenity?.Name ?? String(amenityId);
        sendFilterAppliedEvent(
          SEARCH_FILTER_CATEGORY_MAP.amenity,
          subcategory,
          sortingType
        );
      }
    } else {
      newAmenities = newAmenities.filter((a) => a !== amenityId);
    }
    onFilterChange((prevFilters) => ({ ...prevFilters, amenities: newAmenities }));
  };

  // Fiyat input değişikliği
  const handlePriceMinChange = (e) => {
    const rawValue = e.target.value;
    const formatted = formatPriceDisplay(rawValue);
    setLocalPriceMin(formatted);
    if (priceRangeError) setPriceRangeError('');
  };

  const handlePriceMaxChange = (e) => {
    const rawValue = e.target.value;
    const formatted = formatPriceDisplay(rawValue);
    setLocalPriceMax(formatted);
    if (priceRangeError) setPriceRangeError('');
  };

  // Fiyat arama
  const handlePriceSearch = () => {
    // Backend'e gönderirken temizlenmiş değerleri kullan
    const cleanMin = cleanPriceValue(localPriceMin);
    const cleanMax = cleanPriceValue(localPriceMax);

    const minNum = cleanMin ? Number(cleanMin) : null;
    const maxNum = cleanMax ? Number(cleanMax) : null;
    if (minNum !== null && maxNum !== null && !Number.isNaN(minNum) && !Number.isNaN(maxNum) && minNum > maxNum) {
      setPriceRangeError(tHotel('priceWarning'));
      return;
    }

    const minFormatted = cleanMin ? Number(cleanMin).toLocaleString("tr-TR") : "0";
    const maxFormatted = cleanMax ? Number(cleanMax).toLocaleString("tr-TR") : "∞";
    sendFilterAppliedEvent(
      SEARCH_FILTER_CATEGORY_MAP.price,
      `${minFormatted} - ${maxFormatted} TL`,
      sortingType
    );
    onFilterChange((prevFilters) => ({
      ...prevFilters,
      priceMin: cleanMin || '',
      priceMax: cleanMax || '',
    }));
  };

  // Değerlendirme puanı değişikliği
  const handleRatingChange = (ratingValue) => {
    const subcategory =
      ratingValue === "unrated" ? tHotel('unrated') : ratingValue;
    if (ratingValue) {
      sendFilterAppliedEvent(
        SEARCH_FILTER_CATEGORY_MAP.rating,
        subcategory,
        sortingType
      );
    }
    onFilterChange((prevFilters) => ({ ...prevFilters, rating: ratingValue }));
  };

  // Kampanyalı oteller değişikliği
  const handleDiscountedChange = (checked) => {
    if (checked) {
      sendFilterAppliedEvent(
        SEARCH_FILTER_CATEGORY_MAP.discounted,
        tHotel('onlyDiscountedHotelsFilter'),
        sortingType
      );
    }
    onFilterChange((prevFilters) => ({ ...prevFilters, onlyDiscounted: checked }));
  };

  // Rezervasyon koşulları değişikliği
  const handleRefundableChange = (checked) => {
    if (checked) {
      sendFilterAppliedEvent(
        SEARCH_FILTER_CATEGORY_MAP.refundable,
        tHotel('freeCancellationAvailable'),
        sortingType
      );
    }
    onFilterChange((prevFilters) => ({ ...prevFilters, onlyRefundable: checked }));
  };

  // Statik seçenekler
  const starOptions = [5, 4, 3, 2, 1, 0]; // 0 = Derecelendirilmemiş
  const ratingOptions = [
    { value: '9+', label: tHotel('Excellent'), labelPoint: '9+' },
    { value: '8+', label: tHotel('Great'), labelPoint: '8+' },
    { value: '7+', label: tHotel('VeryGood'), labelPoint: '7+' },
    { value: '6+', label: tHotel('Good'), labelPoint: '6+' },
  ];

  return (
    <aside className="rounded-xl bg-white p-6 shadow-sm pb-6">
      {/* Aktif Filtreler Bölümü */}
      {activeFiltersCount > 0 && (
        <div className="mb-4 flex flex-col gap-3 border-b border-[#e8e8e9] pb-4">
          <div className="flex items-center justify-between">
            <p className="text-base font-semibold text-foreground leading-21">
              {tHotel('filtersSelected', { count: activeFiltersCount })}
            </p>
            <button
              onClick={clearAllFilters}
              className="text-sm leading-19 text-primary"
            >
              {tHotel('clear')}
            </button>
          </div>
          <div className="flex flex-wrap gap-3 ">
            {activeFilters.map((filter) => (
              <Badge
                key={filter.key}
                variant="secondary"
                size="sm"
                className="group flex items-center gap-1 cursor-pointer py-2 pl-[10px] pr-2"
              >
                <span className='text-sm font-normal leading-19 text-primary'>{filter.label}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFilter(filter);
                  }}
                  className="ml-1 "
                  aria-label={tHotel('removeFilter')}
                >
                  <FontAwesomeIcon fontSize={16}
                    icon={byPrefixAndName.far['xmark']}
                    className="h-4! w-4! text-primary cursor-pointer"
                  />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {/* Toplam Oda Fiyatı */}
        <CollapsibleFilterSection title={tHotel('totalRoomPrice')} defaultOpen={true}>
          <div className="flex flex-col gap-4">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <span className="absolute -top-2 left-3 z-1 bg-white px-1 text-xs font-normal text-foreground-muted">
                  {tHotel('minPrice')}
                </span>
                <input
                  type="text"
                  value={localPriceMin}
                  onChange={handlePriceMinChange}
                  className="w-full h-12 px-4 text-base font-medium text-foreground border border-border-strong rounded-md focus:outline-none focus:ring-border-strong focus:border-border-strong"
                />
              </div>
              <div className="flex-1 relative">
                <span className="absolute -top-2 left-3 z-1 bg-white px-1 text-xs font-normal text-foreground-muted">
                  {tHotel('maxPrice')}
                </span>
                <input
                  type="text"
                  value={localPriceMax}
                  onChange={handlePriceMaxChange}
                  className="w-full h-12 px-4 text-base font-medium text-foreground border border-border-strong rounded-md focus:outline-none focus:ring-border-strong focus:border-border-strong"
                />
              </div>
              <Button
                color="primary"
                variant="solid"
                size="default"
                onClick={handlePriceSearch}
                className="!h-12 !max-w-[54px] w-full !px-0"
              >
                <span className="text-sm text-white font-medium">{tHotel('search')}</span>
              </Button>
            </div>
            {!!priceRangeError && (
              <div className="text-sm text-red-600">{priceRangeError}</div>
            )}
          </div>
        </CollapsibleFilterSection>

        {/* İlçe / Bölge */}
        {filterOptions.locations && filterOptions.locations.length > 0 && (
          <CollapsibleFilterSection title={tHotel('districtRegion')} defaultOpen={true}>
            <div className="flex flex-col gap-[14px]">
              {(showAllLocations ? filterOptions.locations : filterOptions.locations.slice(0, 5)).map((location) => (
                <Checkbox
                  key={location.Id}
                  checked={locations?.includes(location.Id) || false}
                  onChange={(e) => handleLocationChange(location.Id, e.target.checked)}
                >
                  <span className="text-sm font-normal text-foreground">{location.Name}</span>
                </Checkbox>
              ))}
              {filterOptions.locations.length > 5 && (
                <button
                  onClick={() => setShowAllLocations(!showAllLocations)}
                  className="text-sm font-medium text-primary text-left cursor-pointer"
                >
                  {showAllLocations ? tHotel('showLess') : tHotel('showMore')}
                </button>
              )}
            </div>
          </CollapsibleFilterSection>
        )}

        {/* Pansiyon Tipi */}
        {filterOptions.boards && filterOptions.boards.length > 0 && (
          <CollapsibleFilterSection title={tHotel('pensionType')} defaultOpen={true}>
            <div className="flex flex-col gap-[14px]">
              {(showAllBoards ? filterOptions.boards : filterOptions.boards.slice(0, 5)).map((board) => (
                <Checkbox
                  key={board.Id}
                  checked={boards?.includes(board.Id) || false}
                  onChange={(e) => handleBoardChange(board.Id, e.target.checked)}
                >
                  <span className="text-sm font-normal text-foreground">{getBoardLabel(board.Id)}</span>
                </Checkbox>
              ))}
              {filterOptions.boards.length > 5 && (
                <button
                  onClick={() => setShowAllBoards(!showAllBoards)}
                  className="text-sm font-medium text-primary text-left cursor-pointer"
                >
                  {showAllBoards ? tHotel('showLess') : tHotel('showMore')}
                </button>
              )}
            </div>
          </CollapsibleFilterSection>
        )}

        {/* Misafir Puanı (Rating) */}
        <CollapsibleFilterSection title={tHotel('guestRating')} defaultOpen={true}>
          <div className="flex flex-col gap-[14px]">
            {ratingOptions.map((ratingOption) => (
              <Radio
                key={ratingOption.value}
                name={ratingGroupName}
                checked={rating === ratingOption.value}
                onChange={(e) => {
                  if (e.target.checked) {
                    handleRatingChange(ratingOption.value);
                  }
                }}
              >
                <div className="flex items-center gap-1">
                  <span className="text-sm font-normal text-foreground font-normal">{ratingOption.label}</span>
                  <span className="text-sm text-foreground font-medium">{ratingOption.labelPoint}</span>
                </div>
              </Radio>
            ))}
            <Radio
              name={ratingGroupName}
              checked={rating === 'unrated'}
              onChange={(e) => {
                if (e.target.checked) {
                  handleRatingChange('unrated');
                }
              }}
            >
              <span className="text-sm font-normal text-foreground">{tHotel('unrated')}</span>
            </Radio>
          </div>
        </CollapsibleFilterSection>

        {/* Kampanyalı Oteller */}
        <CollapsibleFilterSection title={tHotel('campaignHotels')} defaultOpen={true}>
          <div className="flex items-center justify-between gap-3">
            <span
              className="flex-1 text-sm font-normal text-foreground cursor-pointer"
              onClick={() => handleDiscountedChange(!onlyDiscounted)}
            >
              {tHotel('onlyDiscountedHotels')}
            </span>
            <ToggleButton
              checked={onlyDiscounted || false}
              onChange={(e) => handleDiscountedChange(e.target.checked)}
            />
          </div>
        </CollapsibleFilterSection>

        {/* Rezervasyon Koşulları */}
        <CollapsibleFilterSection title={tHotel('reservationConditions')} defaultOpen={true}>
          <div className="flex items-center justify-between gap-3">
            <span
              className="flex-1 text-sm font-normal text-foreground cursor-pointer"
              onClick={() => handleRefundableChange(!onlyRefundable)}
            >
              {tHotel('freeCancellationAvailable')}
            </span>
            <ToggleButton
              checked={onlyRefundable || false}
              onChange={(e) => handleRefundableChange(e.target.checked)}
            />
          </div>
        </CollapsibleFilterSection>

        {/* Yıldız Sayısı */}
        <CollapsibleFilterSection title={tHotel('hotelStars')} defaultOpen={true}>
          <div className="flex flex-col gap-[14px]">
            {starOptions.map((star) => (
              <Checkbox
                key={star}
                checked={stars?.includes(star) || false}
                onChange={(e) => handleStarChange(star, e.target.checked)}
                className="h-6 !items-center"

              >
                <div className="flex items-center gap-2">

                  <span className="text-sm/19 font-normal text-foreground">
                    {star === 0 ? tHotel('unrated') : tHotel('starsFilter', { star })}
                  </span>
                  {star > 0 && (
                    <div className="flex items-center gap-[1px]">
                      {Array.from({ length: star }).map((_, index) => (
                        <span key={index} className='!w-3.5 !h-3.5 flex items-center justify-center'>
                          <FontAwesomeIcon
                            key={index}
                            icon={byPrefixAndName.fas['star']}
                            className="text-yellow !w-[12px] !h-[11px]"
                          />
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Checkbox>
            ))}
          </div>
        </CollapsibleFilterSection>

        {/* Zincir */}
        {filterOptions.chains && filterOptions.chains.length > 0 && (
          <CollapsibleFilterSection title={tHotel('chains')} defaultOpen={true}>
            <div className="flex flex-col gap-[14px]">
              {(showAllChains ? filterOptions.chains : filterOptions.chains.slice(0, 5)).map((chain) => (
                <Checkbox
                  key={chain.Slug}
                  checked={chains?.includes(chain.Slug) || false}
                  onChange={(e) => handleChainChange(chain.Slug, e.target.checked)}
                >
                  <span className="text-sm font-normal text-foreground">{chain.Name}</span>
                </Checkbox>
              ))}
              {filterOptions.chains.length > 5 && (
                <button
                  onClick={() => setShowAllChains(!showAllChains)}
                  className="text-sm font-medium text-primary text-left cursor-pointer"
                >
                  {showAllChains ? tHotel('showLess') : tHotel('showMore')}
                </button>
              )}
            </div>
          </CollapsibleFilterSection>
        )}

        {/* Temalar */}
        {filterOptions.themes && filterOptions.themes.length > 0 && (
          <CollapsibleFilterSection title={tHotel('themes')} defaultOpen={true}>
            <div className="flex flex-col gap-[14px]">
              {(showAllThemes ? filterOptions.themes : filterOptions.themes.slice(0, 5)).map((theme) => (
                <Checkbox
                  key={theme.Id}
                  checked={themes?.includes(theme.Id) || false}
                  onChange={(e) => handleThemeChange(theme.Id, e.target.checked)}
                >
                  <span className="text-sm font-normal text-foreground">{theme.Name}</span>
                </Checkbox>
              ))}
              {filterOptions.themes.length > 5 && (
                <button
                  onClick={() => setShowAllThemes(!showAllThemes)}
                  className="text-sm font-medium text-primary text-left cursor-pointer"
                >
                  {showAllThemes ? tHotel('showLess') : tHotel('showMore')}
                </button>
              )}
            </div>
          </CollapsibleFilterSection>
        )}

        {/* Tesis Özellikleri */}
        {filterOptions.amenities && filterOptions.amenities.length > 0 && (
          <CollapsibleFilterSection title={tHotel('facilityFeatures')} defaultOpen={true}>
            <div className="grid grid-cols-2 gap-x-[14px] gap-y-4">
              {(showAllAmenities ? filterOptions.amenities : filterOptions.amenities.slice(0, 8)).map((amenity) => {
                const isChecked = amenities?.includes(amenity.Id) || false;
                // Amenity Code'u bul (Code, AmenityId veya Id alanından)
                const amenityCode = amenity.Code || amenity.AmenityId || amenity.Id;
                const iconName = amenityIconMap[amenityCode] || "home";
                // Öncelik sırası: fak (custom) -> fas (FontAwesome solid) -> fallback home
                const icon = byPrefixAndName.fak[iconName] || byPrefixAndName.fal[iconName] || byPrefixAndName.fal["home"];

                return (
                  <button
                    key={amenity.Id}
                    type="button"
                    onClick={() => handleAmenityChange(amenity.Id, !isChecked)}
                    className={clsx(
                      "flex flex-col items-center justify-center gap-[6px] px-3 py-[10px] rounded-lg border border-border transition-all duration-200 cursor-pointer",
                      "hover:border-primary hover:bg-primary-weaker",
                      isChecked
                        ? "border-primary bg-primary-weaker"
                        : "border-border-strong bg-white"
                    )}
                  >
                    <FontAwesomeIcon
                      icon={icon}
                      className={clsx(
                        "text-xl w-6! h-6!",
                        isChecked ? "text-primary" : "text-foreground"
                      )}
                    />
                    <span className={clsx(
                      "text-sm font-medium text-center",
                      isChecked ? "text-primary" : "text-foreground"
                    )}>
                      {amenity.Name}
                    </span>
                  </button>
                );
              })}
              {filterOptions.amenities.length > 8 && (
                <button
                  onClick={() => setShowAllAmenities(!showAllAmenities)}
                  className="text-sm font-medium text-primary text-left cursor-pointer col-span-2"
                >
                  {showAllAmenities ? tHotel('showLess') : tHotel('showMore')}
                </button>
              )}
            </div>
          </CollapsibleFilterSection>
        )}
      </div>
    </aside>
  );
}

/**
 * Collapsible filtre bölümü component'i
 */
function CollapsibleFilterSection({ title, children, defaultOpen = true }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0 flex flex-col gap-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between text-left cursor-pointer py-1"
      >
        <h3 className="text-base text-foreground font-semibold">{title}</h3>
        <FontAwesomeIcon
          icon={byPrefixAndName.far['chevron-down']}
          className={clsx(
            'h-5 w-5 text-foreground transition-transform duration-200 flex-shrink-0',
            isOpen && 'rotate-180'
          )}
        />
      </button>
      {isOpen && <div className="">{children}</div>}
    </div>
  );
}
